import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useRef, useState } from 'react'
import useToastr from '../../../../../hooks/useToastr'
import GMAWBUsersService from '../../../../../services/GMAWBUsersService'
import { E2EUserPaginatedModel } from '../../../../../shared/models/E2EUserPaginatedModel'
import { MailModel } from '../../../../../shared/models/Mail.model'
import HandlerType from '../../enums/handler-type'
import { useSaveHandler } from '../../hooks/useSaveHandler'
import PmrE2EProjectDetailsOrderlineModel from '../../interfaces/pmr-e2e-project-details-orderline-model'
import HandlerAssignmentNotificationEmailEditorDialog, {
  HandlerAssignmentNotificationEmailEditorDialogRef,
} from '../handler-assignment-notification-email-editor-dialog/HandlerAssignmentNotificationEmailEditorDialog'
import { useHandlerAssignmentNotificationEmailEditor } from '../handler-assignment-notification-email-editor-dialog/useHandlerAssignmentNotificationEmailEditor'
import ProjectCollaboratorAssignmentDropdown from '../project-collaborator-assignment-dialog/project-collaborator-assignment-dropdown/ProjectCollaboratorAssignmentDropdown'
import classes from './PmrBulkHandlerReassignmentDialog.module.scss'
import { Nullable } from '../../../../../utils/helpers/ts.helpers'

interface PmrHandlerReassignmentDialogProps {
  projectId: string
  orderLineIds: Array<string>
  handlerType: HandlerType
  visible: boolean
  onClose: () => void
  getList: () => void
  resetSelectedLines: () => void
  isEmailEditable: boolean
  orderlines: PmrE2EProjectDetailsOrderlineModel[]
}

interface ProjectUserSource {
  currentSkip: number
  currentTake: number
  currentSearchPhrase: string
  data: E2EUserPaginatedModel[]
}

const initDropdownValues: ProjectUserSource = {
  currentSkip: 0,
  currentTake: 5,
  currentSearchPhrase: '',
  data: [],
}
const PmrBulkHandlerReassignmentDialog = (props: PmrHandlerReassignmentDialogProps) => {
  const [selectedHandler, setSelectedHandler] = useState<E2EUserPaginatedModel>()
  const [handlerDropdownOptions, setHandlerDropdownOptions] =
    useState<ProjectUserSource>(initDropdownValues)
  const [isDropdownLoading, setIsDropdownLoading] = useState<boolean>(false)
  const emailEditorRef = useRef<HandlerAssignmentNotificationEmailEditorDialogRef | null>(null)
  const {
    emailEditorVisible,
    openEmailEditor,
    closeEmailEditor,
    emailEditorOrderlines,
    showCancelToastNotification,
    setEmailEditorVisible,
    showSaveAndCloseToastNotification,
  } = useHandlerAssignmentNotificationEmailEditor({
    onSuppressed: () => onHandleSave(),
    emailEditorRef: emailEditorRef,
  })
  const { getLabelByHandlerType, isSavingHandler, saveHandler } = useSaveHandler({
    onSuccess: () => {
      props.getList()
      props.resetSelectedLines()
      props.onClose()
    },
  })
  const handlerTypeName = getLabelByHandlerType(props.handlerType)

  useEffect(() => {
    setIsDropdownLoading(true)
    GMAWBUsersService.getPaginatedUsers(0, 5, props.handlerType, false, '').then(response => {
      let flexData: E2EUserPaginatedModel[] = structuredClone(response?.data)

      setHandlerDropdownOptions(current => ({
        ...current,
        data: flexData,
      }))

      setIsDropdownLoading(false)
    })
  }, [])

  const onHandleSave = async (customMail?: MailModel) => {
    closeEmailEditor()
    if (!selectedHandler) return
    await saveHandler(props.orderlines, props.handlerType, selectedHandler, customMail)
  }

  const onCancel = () => {
    props.onClose()
  }

  const handleEmailEditingCancel = (_: Nullable<string>, isFromSaveAndClose?: boolean) => {
    closeEmailEditor()

    if (isFromSaveAndClose) showSaveAndCloseToastNotification()
    else showCancelToastNotification()

    onCancel()
  }

  const footer = (
    <div className={classes['footer']}>
      <div>
        <Button
          className={`${classes['button-cancel']}`}
          label="CANCEL"
          onClick={onCancel}
          disabled={isDropdownLoading}
        />
      </div>
      <div className={`${classes['button-save-wrapper']}`}>
        <Button
          className={`${classes['button-save']}`}
          label="SAVE"
          onClick={() =>
            props.isEmailEditable
              ? openEmailEditor(props.orderlines, selectedHandler!)
              : onHandleSave()
          }
          disabled={isDropdownLoading || isSavingHandler || selectedHandler === undefined}
          loading={isSavingHandler}
        />
      </div>
    </div>
  )

  const header = (
    <div className={`${classes['header']}`}>
      <span>Assign {handlerTypeName}</span>
    </div>
  )

  return (
    <>
      <Dialog
        draggable={false}
        resizable={false}
        className={classes['pmr-handler-reassignment-dialog']}
        header={header}
        footer={footer}
        visible={props.visible}
        style={{ width: '30rem' }}
        modal
        onHide={onCancel}
        contentClassName={classes['content']}
      >
        {isDropdownLoading && (
          <div className={`${classes['spinner__container']}`}>
            <ProgressSpinner
              strokeWidth="5"
              animationDuration="5s"
              className={`${classes['spinner']}`}
            />
            <div>Loading Contributors.</div>
          </div>
        )}
        {!isDropdownLoading && (
          <div className={classes['dropdown']}>
            <ProjectCollaboratorAssignmentDropdown
              value={selectedHandler}
              dropdownOptions={handlerDropdownOptions}
              setValue={setSelectedHandler}
              setDropdownOptions={setHandlerDropdownOptions}
              handlerType={props.handlerType}
              isGmaTcTask={false}
              isDisabled={isSavingHandler}
              removeAppendToSelf={true}
            />
          </div>
        )}
      </Dialog>
      <HandlerAssignmentNotificationEmailEditorDialog
        visible={emailEditorVisible}
        onHide={handleEmailEditingCancel}
        selectedNewHandler={selectedHandler}
        orderlines={emailEditorOrderlines}
        onSend={onHandleSave}
        ref={emailEditorRef}
        onMinimize={() => {
          setEmailEditorVisible(false)
          props.onClose()
        }}
        handlerType={props.handlerType}
      />
    </>
  )
}

export default PmrBulkHandlerReassignmentDialog
